import clsx from 'clsx';
import type { ReactNode, Ref } from 'react';
import { Fragment, forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Observable, Subject, takeUntil } from 'rxjs';
import { OdinDataSender } from '../../common/data-retrieval/odin-data-sender';
import useTestTag from '../../common/hooks/use-test-tag';
import Button from '../button/button';
import OdinTransition from '../common/odin-transition/odin-transition.component';
import { getAllElementStyleValues, mergeStyling } from '../common-functions';
import { useIsMountedRef } from '../common-hooks';
import type { ModalDialogButtonSetting, ModalDialogResult } from '../common-interfaces';
import { DomHandler } from '../dom-handler';
import { ScreenPosition } from '../enums';
import { bindDocumentKeyListener, unbindDocumentClickListener, unbindDocumentKeyListener } from '../event-handlers';
import OdinIcon from '../odin-icon/odin-icon.component';
import type { ModalDialogContextProps } from './modal-dialog.context';
import { ModalDialogContext } from './modal-dialog.context';

export interface ModalDialogProps {
  visible?: boolean;
  displayPosition?: ScreenPosition;
  transitionDuration?: number;
  showTransitionOptions?: string;
  hideTransitionOptions?: string;
  minDialogWidth?: number | string;
  maxDialogWidth?: number | string;
  minDialogHeight?: number | string;
  maxDialogHeight?: number | string;
  fullScreen?: boolean;
  showCloseIcon?: boolean;
  closeOnEscape?: boolean;
  mountOnEnter?: boolean;
  unmountOnExit?: boolean;
  header: string | React.JSX.Element;
  footer?: string | React.JSX.Element;
  shown?: () => void;
  hidden?: () => void;
  buttons?: Array<ModalDialogButtonSetting>;
  buttonsCloseModal?: boolean;
  buttonType?: 'button' | 'submit';
  children: ReactNode | Array<ReactNode>;
  shouldCheckZIndex?: boolean;
  renderChildrenLazily?: boolean;
  contentClassName?: string;
}

export interface ModalDialogRef {
  show: () => undefined | Observable<ModalDialogResult>;
  close: () => void;
  subscribeToDialogResult: () => Observable<ModalDialogResult>;
  dataSender?: OdinDataSender<ModalDialogContextProps>;
}

export interface ModalDialogListeners {
  clickListener?: (event: MouseEvent) => void;
  keyListener?: (event: KeyboardEvent) => void;
}

function ModalDialog(props: ModalDialogProps, ref: Ref<ModalDialogRef>) {
  const {
    visible = false,
    displayPosition = ScreenPosition.Center,
    transitionDuration = 400,
    showTransitionOptions = 'linear',
    hideTransitionOptions = 'linear',
    minDialogWidth = 300,
    maxDialogWidth = 700,
    minDialogHeight,
    maxDialogHeight,
    fullScreen = false,
    showCloseIcon = true,
    closeOnEscape = true,
    mountOnEnter = true,
    unmountOnExit = true,
    header,
    footer,
    shown,
    hidden,
    buttons,
    buttonsCloseModal = true,
    buttonType = 'button',
    children,
    shouldCheckZIndex = false,
    renderChildrenLazily = false,
    contentClassName,
  } = props;

  const [top, setTop] = useState(0);
  const [left, setLeft] = useState(0);
  const [underlayZIndex, setUnderlayZIndex] = useState(-1);
  const [dialogZIndex, setDialogZIndex] = useState(-1);
  const [visibility, setVisibility] = useState(visible || false);
  const [modalData, setModalData] = useState<ModalDialogContextProps>({ data: {} });

  const destroySubject = useRef(new Subject<void>());
  const dataSender = useRef<OdinDataSender<ModalDialogContextProps> | undefined>(undefined);
  const resizeObserver = useRef<ResizeObserver | undefined>(undefined);
  const dialogRef = useRef<HTMLDivElement>(null);
  const listenerRef = useRef<ModalDialogListeners>({});
  const dialogResultRef = useRef<Subject<ModalDialogResult>>(new Subject<ModalDialogResult>());
  const isMounted = useIsMountedRef();

  const defaultUnderlayModalDialogZIndex = useRef(101);
  const defaultModalDialogZIndex = useRef(102);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Mount-only effect for resize observer setup
  useEffect(() => {
    new Observable<ModalDialogContextProps>((subscriber) => {
      dataSender.current = new OdinDataSender<ModalDialogContextProps>(subscriber);
    })
      .pipe(takeUntil(destroySubject.current))
      .subscribe((data) => setModalData(data));

    resizeObserver.current = new ResizeObserver(() => {
      if (!fullScreen && dialogRef.current && isMounted.current) {
        const { top, left } = DomHandler.positionToScreen(dialogRef.current, displayPosition);
        setTop(top);
        setLeft(left);
      }
    });

    // Capture ref values for cleanup
    const destroySubjectCurrent = destroySubject.current;
    const resizeObserverCurrent = resizeObserver.current;

    return () => {
      destroySubjectCurrent.next();
      destroySubjectCurrent.complete();

      resizeObserverCurrent?.disconnect();
    };
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: setZIndexValues is stable, isMounted is a ref
  useEffect(() => {
    if (isMounted.current) {
      setVisibility(visible);
      if (visible) {
        setZIndexValues();
      } else {
        setUnderlayZIndex(-1);
        setDialogZIndex(-1);
      }
    }
  }, [visible]);

  const tagCreator = useTestTag('modal');

  const setZIndexValues = useCallback(() => {
    let allZIndexes: Array<number> = [];

    if (shouldCheckZIndex) {
      allZIndexes = getAllElementStyleValues('zIndex', (styleValue) => {
        const childZIndex = parseInt(styleValue);
        return childZIndex >= 100;
      }).map((item) => parseInt(item));
    }

    if (allZIndexes.length > 0) {
      setUnderlayZIndex(Math.max(...allZIndexes) + 1);
      setDialogZIndex(Math.max(...allZIndexes) + 2);
    } else {
      setUnderlayZIndex(defaultUnderlayModalDialogZIndex.current);
      setDialogZIndex(defaultModalDialogZIndex.current);
    }
  }, [shouldCheckZIndex]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: isMounted is a ref, not a dependency
  const onEntering = useCallback(() => {
    if (!dialogRef.current || !isMounted.current) return;

    if (!fullScreen) {
      const { top, left } = DomHandler.positionToScreen(dialogRef.current, displayPosition);
      setTop(top);
      setLeft(left);
    } else {
      setTop(0);
      setLeft(0);
    }
  }, [fullScreen, displayPosition]);

  const onEntered = () => {
    if (!dialogRef.current || !isMounted.current) return;

    resizeObserver.current?.observe(dialogRef.current);
    shown?.();

    if (underlayZIndex === -1 || dialogZIndex === -1) {
      setUnderlayZIndex(defaultUnderlayModalDialogZIndex.current);
      setDialogZIndex(defaultModalDialogZIndex.current);
    }

    if (closeOnEscape) {
      listenerRef.current.keyListener = bindDocumentKeyListener(['Escape'], (isKeyNamePressed) => {
        if (isKeyNamePressed) {
          setVisibility(false);
        }
      });
    }
  };

  const onExit = useCallback(() => {
    if (listenerRef.current.clickListener) {
      unbindDocumentClickListener(listenerRef.current.clickListener);
    }
    if (listenerRef.current.keyListener) {
      unbindDocumentKeyListener(listenerRef.current.keyListener);
    }

    if (dialogRef.current) {
      resizeObserver.current?.unobserve(dialogRef.current);
    }
  }, []);

  const onExited = () => {
    if (!isMounted.current) return;

    setDialogZIndex(-1);
    setUnderlayZIndex(-1);

    hidden?.();
  };

  const onCloseClick = useCallback(() => {
    close();
    dialogResultRef.current.next({
      buttonName: 'close',
    });
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: isMounted is a ref that doesn't change
  const onResultButtonClick = useCallback(
    (buttonName: string, closeModalOnClick = true) => {
      if (!isMounted.current) return;

      if (buttonsCloseModal && closeModalOnClick) {
        setVisibility(false);
      }

      dialogResultRef.current.next({
        buttonName,
      });
    },
    [buttonsCloseModal]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: isMounted is a ref, setZIndexValues is stable
  const show = useCallback(() => {
    if (!isMounted.current) return;

    setVisibility(true);
    setZIndexValues();
    return dialogResultRef.current.asObservable();
  }, [setZIndexValues]);

  const close = () => setVisibility(false);

  const subscribeToDialogResult = () => dialogResultRef.current.asObservable();

  useImperativeHandle(ref, () => ({
    show,
    close,
    subscribeToDialogResult,
    dataSender: dataSender.current,
  }));

  const renderHeader = useCallback(() => {
    return (
      <div className="items-top flex shrink px-4 pb-2 pt-3">
        <div className="grow truncate text-base font-medium text-mono-0">{header}</div>
        {showCloseIcon && (
          <div
            className="-mr-2 -mt-1 flex shrink cursor-pointer items-start pl-2 text-primary-2"
            onClick={onCloseClick}
            ref={tagCreator('close-icon')}
            data-testid="modal-close-icon"
          >
            <OdinIcon icon="X" />
          </div>
        )}
      </div>
    );
  }, [header, showCloseIcon, onCloseClick, tagCreator]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: renderFooterButton is defined after this callback
  const renderFooter = useCallback(() => {
    if (buttons || footer) {
      let renderedButtons: React.JSX.Element = <></>;
      let leftAlignedButtons: ModalDialogButtonSetting[] = [];

      if (buttons) {
        leftAlignedButtons = buttons.filter((button) => button.alignment === 'left');
        const rightAlignedButtons = buttons.filter((button) => button.alignment !== 'left');

        const buttonsWrapperClasses = clsx('flex items-center justify-between', {
          'w-full': leftAlignedButtons.length > 0 || !footer,
        });

        renderedButtons = (
          <div className={buttonsWrapperClasses}>
            <div className="flex items-center justify-center gap-x-2 self-start">
              {leftAlignedButtons.map((button) => (
                <Fragment key={button.name}>{renderFooterButton(button)}</Fragment>
              ))}
            </div>
            <div className="flex items-center justify-center gap-x-2 self-end">
              {rightAlignedButtons.map((button) => (
                <Fragment key={button.name}>{renderFooterButton(button)}</Fragment>
              ))}
            </div>
          </div>
        );
      }

      const footerClasses = clsx(
        'truncate p-1',
        { grow: leftAlignedButtons.length > 0 },
        { 'flex-1': leftAlignedButtons.length === 0 }
      );

      return (
        <div className="flex border-t border-solid border-primary-5 p-4">
          {footer && <div className={footerClasses}>{footer}</div>}
          {renderedButtons}
        </div>
      );
    }
  }, [buttons, footer]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: isMounted is a ref that doesn't change
  const renderFooterButton = useCallback(
    (button: ModalDialogButtonSetting) => (
      <div className="flex-auto" ref={tagCreator(`${button.text?.replace(/\s+/g, '-').toLowerCase()}-button`)}>
        <Button
          htmlType={buttonType}
          classNames={button.classNames}
          type={button.type}
          variant={button.variant}
          form={button.form}
          disabled={button.disabled}
          onClick={() => {
            if (isMounted.current) {
              button.onClick?.();
              onResultButtonClick(button.name, button.closeModalOnClick);
            }
          }}
        >
          {button.text}
        </Button>
      </div>
    ),
    [buttonType, onResultButtonClick, tagCreator]
  );

  const createDialog = () => {
    const marginOffset = fullScreen ? 0 : 32;
    const baseStyles = {
      maxWidth: maxDialogWidth,
      maxHeight: maxDialogHeight || `calc(100vh - ${marginOffset * 2}px)`,
      minWidth: minDialogWidth,
      minHeight: minDialogHeight,
    };

    return (
      <OdinTransition
        start={visibility}
        timeout={transitionDuration}
        showTransitionOptions={showTransitionOptions}
        hideTransitionOptions={hideTransitionOptions}
        mountOnEnter={mountOnEnter}
        unmountOnExit={unmountOnExit}
        onEntering={onEntering}
        onEntered={onEntered}
        onExit={onExit}
        onExited={onExited}
      >
        <>
          <div>
            <div
              className="fixed left-0 top-0 h-full w-full bg-mono-0"
              onClick={() => {
                setVisibility(false);
              }}
              style={{
                zIndex: underlayZIndex,
                opacity: 0.5,
              }}
              data-testid="modal-backdrop"
            />
            {(renderChildrenLazily ? visibility : true) && (
              <div
                className={clsx('fixed max-w-full', {
                  'h-full w-full': fullScreen,
                })}
                style={{
                  padding: marginOffset,
                  zIndex: dialogZIndex,
                  top,
                  left,
                }}
                ref={dialogRef}
              >
                <div
                  className={clsx('flex flex-col bg-mono-1 shadow-lg rounded-md', {
                    'relative left-[2.5%] top-[2.5%] h-[90vh] w-[95vw] sm:h-[95vh]': fullScreen,
                  })}
                  style={fullScreen ? {} : { ...baseStyles }}
                  ref={tagCreator('container')}
                >
                  {renderHeader()}
                  <ModalDialogContext.Provider value={modalData}>
                    <div
                      className={mergeStyling('custom-scroll grow overflow-auto px-4 pb-4', contentClassName)}
                      ref={tagCreator('content')}
                    >
                      {children}
                    </div>
                  </ModalDialogContext.Provider>
                  {renderFooter()}
                </div>
              </div>
            )}
          </div>
        </>
      </OdinTransition>
    );
  };

  return ReactDOM.createPortal(createDialog(), document.body);
}

export default forwardRef(ModalDialog);
